import React, { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { TranslationKey, useTranslation } from '../../translations';
import { Fetcher, useFetcher } from '../../utils';
import { LinkButton } from '../LinkButton';
import { IconColor, MaterialIcon } from '../MaterialIcon';

export const CartComplete: FC = () => {
  const { ts } = useTranslation();
  const params = useParams<{ id: string }>();

  const fetcher = useFetcher(async () => {
    return await api.getOrderStatus(Number.parseInt(params.id, 10));
  }, [params]);

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  const order = fetcher.data;

  if (order.status === 'paid') {
    return (
      <OrderStatusCard
        icon="check_circle"
        iconType="success"
        subText={[ts('complete.order_completed'), ts('complete.view_order')]}
        title="complete.payment_success"
      >
        <Col className="mb-10" md={3} xs={12}>
          <LinkButton block color="secondary" outline to="/">
            {ts('complete.go_home')}
          </LinkButton>
        </Col>
      </OrderStatusCard>
    );
  }

  if (!order.paymentError) {
    return (
      <OrderStatusCard
        icon="sync_problem"
        iconType="warning"
        // Check i18n
        subText={[
          ts('complete.payment_not_complete'),
          `Try to refresh the page, or contact support. (Order#${params.id})`,
        ]}
        title="complete.payment_error"
      >
        <Col className="mb-10" md={3} xs={12}>
          <LinkButton block color="secondary" outline to="/cart">
            {ts('common.go_back')}
          </LinkButton>
        </Col>
      </OrderStatusCard>
    );
  }

  return (
    <OrderStatusCard
      icon="error"
      iconType="danger"
      subText={[
        ts('complete.payment_not_completed'),
        `${order.paymentError.error} (${order.paymentError.code})`,
        ts('complete.back_to_checkout'),
      ]}
      title="complete.payment_error"
    >
      <Col className="mb-10" md={3} xs={12}>
        <LinkButton block color="secondary" outline to="/cart">
          {ts('common.go_back')}
        </LinkButton>
      </Col>
    </OrderStatusCard>
  );
};

interface OrderStatusCardProps {
  readonly children?: ReactNode;
  readonly icon: string;
  readonly iconType: IconColor;
  readonly title: TranslationKey;
  readonly subText: string[];
}

const OrderStatusCard: FC<OrderStatusCardProps> = ({
  icon,
  iconType,
  title,
  subText,
  children,
}) => {
  const { ts } = useTranslation();
  return (
    <Row className="justify-content-center">
      <Col className="mb-10" id="cartComplete" lg={10} xs={12}>
        <Card className="mb-10">
          <CardBody className="text-center">
            <MaterialIcon large name={icon} type={iconType} />
            <h3 className={iconType === 'success' ? 'text-success' : ''}>{ts(title)}</h3>
            {subText.map((t) => (
              <p key={t}>{t}</p>
            ))}
            <Row className="justify-content-center">{children}</Row>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
